import { Deferred, Head, Link, router, usePage } from "@inertiajs/react";
import type { ColumnDef, ExpandedState, Row } from "@tanstack/react-table";
import { type ContextFn, format, type Locale } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import {
	Check,
	ChevronDown,
	ChevronsUpDown,
	ChevronUp,
	ListFilterIcon,
	Loader2,
	MoreHorizontal,
	PencilIcon,
	PlusIcon,
	ReceiptTextIcon,
	Trash2Icon,
	Upload,
	XIcon,
} from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ApptPagination from "@/components/admin-portal/appointment/pagination-list";
import { PageContainer } from "@/components/admin-portal/shared/page-layout";
import BulkUploadDialog from "@/components/admin-portal/vouchers/bulk-upload-dialog";
import NewVoucherFormFields from "@/components/admin-portal/vouchers/upsert-form-fields";
import VoucherOrdersDialog from "@/components/admin-portal/vouchers/voucher-orders-dialog";
import { useDateContext } from "@/components/providers/date-provider";
import {
	ResponsiveDialog,
	type ResponsiveDialogProps,
} from "@/components/shared/responsive-dialog";
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import { DataTable } from "@/components/ui/data-table";
import { DataTableColumnHeader } from "@/components/ui/data-table/column-header";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import {
	cn,
	formatCurrency,
	formatPercentage,
	populateQueryParams,
} from "@/lib/utils";
import type { Package } from "@/types/admin-portal/package";
import type { Voucher } from "@/types/admin-portal/vouchers";
import type { GlobalPageProps as BaseGlobalPageProps } from "@/types/globals";
import type { Metadata } from "@/types/pagy";

export interface PageProps {
	vouchers: Voucher[];
	vouchersMeta?: Metadata | null;
	selectedVoucher?: Voucher | null;
	packages?: Package[] | null;
	voucherOwners?: { id: string; name: string }[] | null;
	bonusRoEditable: boolean;
}

export interface VoucherIndexGlobalPageProps
	extends BaseGlobalPageProps,
		PageProps {
	[key: string]: any;
}

export default function Index({
	vouchers,
	vouchersMeta,
	selectedVoucher,
	packages,
	voucherOwners,
	bonusRoEditable,
}: PageProps) {
	const { props: globalProps, url: pageURL } =
		usePage<VoucherIndexGlobalPageProps>();
	const { locale, tzDate } = useDateContext();
	const { t: tv } = useTranslation("vouchers");
	const vouchersIndexRoute =
		globalProps.adminPortal?.router?.adminPortal?.vouchers?.index;

	const { queryParams } = useMemo(
		() => populateQueryParams(pageURL),
		[pageURL],
	);
	const selectedId = queryParams?.id;

	const vouchersData = useMemo(
		() => (Array.isArray(vouchers) ? vouchers : []),
		[vouchers],
	);

	const packagesData = useMemo<Package[]>(() => {
		if (Array.isArray(packages)) return packages;
		const fallback = globalProps?.packages;
		return Array.isArray(fallback) ? (fallback as Package[]) : [];
	}, [packages, globalProps?.packages]);

	const packagesById = useMemo<Record<string, Package>>(
		() =>
			packagesData.reduce<Record<string, Package>>((acc, pkg) => {
				acc[String(pkg.id)] = pkg;
				return acc;
			}, {}),
		[packagesData],
	);

	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [voucherIdPendingDelete, setVoucherIdPendingDelete] = useState<
		string | number | null
	>(null);
	const [isDeleteLoading, setIsDeleteLoading] = useState(false);

	// Bulk upload state from query params
	const isBulkUploadOpen = useMemo(
		() => globalProps.adminPortal?.currentQuery?.bulkUpload === "true",
		[globalProps.adminPortal?.currentQuery],
	);

	const voucherPendingDelete = useMemo(
		() =>
			voucherIdPendingDelete == null
				? null
				: (vouchersData.find(
						(voucher) => String(voucher.id) === String(voucherIdPendingDelete),
					) ?? null),
		[voucherIdPendingDelete, vouchersData],
	);

	const currentExpanded = useMemo<ExpandedState>(() => {
		if (!selectedId) return {};

		const rowIndex = vouchersData.findIndex(
			(voucher) => String(voucher.id) === String(selectedId),
		);

		if (rowIndex < 0) return {};

		return { [rowIndex]: true };
	}, [selectedId, vouchersData]);

	const handleRowExpandToggle = useCallback(
		(row: Row<Voucher>) => {
			const nextId = row.getIsExpanded() ? null : row.original.id;
			row.toggleExpanded();

			const { fullUrl, queryParams } = populateQueryParams(pageURL, {
				id: nextId,
			});

			router.get(fullUrl, queryParams, {
				only: ["flash", "adminPortal", "selectedVoucher", "packages"],
				preserveScroll: true,
				preserveState: true,
				replace: true,
			});
		},
		[pageURL],
	);

	const handleRowClose = useCallback(
		(row: Row<Voucher>) => {
			row.toggleExpanded(false);

			const { fullUrl, queryParams } = populateQueryParams(pageURL, {
				id: null,
			});

			router.get(fullUrl, queryParams, {
				only: ["flash", "adminPortal", "selectedVoucher"],
				preserveScroll: true,
				preserveState: true,
				replace: true,
			});
		},
		[pageURL],
	);

	const handleEditVoucher = useCallback(
		(voucherId: string | number) => {
			const { fullUrl, queryParams } = populateQueryParams(pageURL, {
				edit: voucherId,
				id: voucherId,
			});

			router.get(
				fullUrl,
				{ ...queryParams },
				{
					only: ["flash", "adminPortal", "selectedVoucher", "packages"],
					preserveScroll: true,
					preserveState: true,
					replace: true,
				},
			);
		},
		[pageURL],
	);

	const handleDeleteVoucher = useCallback((voucherId: string | number) => {
		setVoucherIdPendingDelete(voucherId);
		setIsDeleteLoading(false);
		setIsDeleteDialogOpen(true);
	}, []);

	const handleBulkUploadOpen = useCallback(() => {
		const { fullUrl, queryParams } = populateQueryParams(pageURL, {
			bulkUpload: "true",
		});

		router.get(
			fullUrl,
			{ ...queryParams },
			{
				only: ["flash", "adminPortal"],
				preserveScroll: true,
				preserveState: true,
				replace: true,
			},
		);
	}, [pageURL]);

	const handleBulkUploadClose = useCallback(() => {
		const { fullUrl, queryParams } = populateQueryParams(pageURL, {
			bulkUpload: null,
		});

		router.get(
			fullUrl,
			{ ...queryParams },
			{
				only: ["flash", "adminPortal"],
				preserveScroll: true,
				preserveState: true,
				replace: true,
			},
		);
	}, [pageURL]);

	const handleDeleteDialogOpenChange = useCallback(
		(open: boolean) => {
			if (!open && isDeleteLoading) {
				return;
			}
			setIsDeleteDialogOpen(open);
			if (!open) {
				setVoucherIdPendingDelete(null);
			}
		},
		[isDeleteLoading],
	);

	const handleConfirmDelete = useCallback(() => {
		if (!voucherIdPendingDelete || !vouchersIndexRoute) return;

		router.delete(`${vouchersIndexRoute}/${voucherIdPendingDelete}`, {
			preserveScroll: true,
			preserveState: true,
			onStart: () => setIsDeleteLoading(true),
			onFinish: () => {
				setIsDeleteLoading(false);
				setIsDeleteDialogOpen(false);
				setVoucherIdPendingDelete(null);
			},
		});
	}, [voucherIdPendingDelete, vouchersIndexRoute]);

	const changePage = useCallback(
		(
			type: "prev" | "next" | "first" | "last" | "limit" | "page",
			value?: string,
		) => {
			if (!vouchersMeta) return;

			let url: string | undefined;
			switch (type) {
				case "prev":
					url = vouchersMeta.prevUrl;
					break;
				case "next":
					url = vouchersMeta.nextUrl;
					break;
				case "first":
					url = vouchersMeta.firstUrl;
					break;
				case "last":
					url = vouchersMeta.lastUrl;
					break;
				case "limit": {
					if (!value) return;
					const { fullUrl } = populateQueryParams(vouchersMeta.pageUrl, {
						limit: value,
						page: 1,
					});
					url = fullUrl;
					break;
				}
				case "page": {
					if (!value) return;
					const { fullUrl } = populateQueryParams(vouchersMeta.pageUrl, {
						page: value,
					});
					url = fullUrl;
					break;
				}
			}

			if (!url) return;

			router.get(
				url,
				{},
				{
					preserveScroll: false,
					preserveState: true,
					only: ["vouchers", "vouchersMeta", "adminPortal", "flash", "errors"],
				},
			);
		},
		[vouchersMeta],
	);

	const columns = useMemo<ColumnDef<Voucher>[]>(
		() =>
			getColumns(handleRowExpandToggle, {
				locale,
				tzDate,
				onEditVoucher: handleEditVoucher,
				onDeleteVoucher: handleDeleteVoucher,
			}),
		[
			handleDeleteVoucher,
			handleEditVoucher,
			handleRowExpandToggle,
			locale,
			tzDate,
		],
	);

	const formDialogMode = useMemo(() => {
		const isCreateMode =
			globalProps.adminPortal?.currentQuery?.new === "voucher";
		const editVoucherId = globalProps.adminPortal?.currentQuery?.edit ?? null;
		return { isCreateMode, editVoucherId };
	}, [globalProps.adminPortal?.currentQuery]);

	const createVoucherDialog = useMemo<ResponsiveDialogProps>(() => {
		return {
			title: "Create Voucher",
			description: "Fill in the details to create a new voucher.",
			isOpen: formDialogMode.isCreateMode,
			dialogWidth: "640px",
			onOpenChange: () => {
				const { fullUrl, queryParams } = populateQueryParams(pageURL, {
					new: null,
				});

				router.get(
					fullUrl,
					{ ...queryParams },
					{
						only: ["flash", "adminPortal", "packages"],
						preserveScroll: true,
						preserveState: true,
						replace: true,
					},
				);
			},
		};
	}, [formDialogMode.isCreateMode, pageURL]);

	const editVoucherDialog = useMemo<ResponsiveDialogProps>(() => {
		const isOpen = Boolean(formDialogMode.editVoucherId);
		return {
			title: "Edit Voucher",
			description: "Update the voucher details.",
			isOpen,
			dialogWidth: "640px",
			onOpenChange: () => {
				const { fullUrl, queryParams } = populateQueryParams(pageURL, {
					edit: null,
					id: null,
					owner_id: null,
				});

				router.get(
					fullUrl,
					{ ...queryParams },
					{
						only: ["flash", "adminPortal", "selectedVoucher", "packages"],
						preserveScroll: true,
						preserveState: true,
						replace: true,
					},
				);
			},
		};
	}, [formDialogMode.editVoucherId, pageURL]);

	const voucherForEdit =
		formDialogMode.editVoucherId &&
		selectedVoucher &&
		String(selectedVoucher.id) === String(formDialogMode.editVoucherId)
			? selectedVoucher
			: undefined;

	return (
		<>
			<Head title={tv("head_title")} />

			<PageContainer className="min-h-[100vh] flex-1 md:min-h-min space-y-4">
				<div className="flex flex-col justify-between gap-4 md:flex-row">
					<div>
						<h1 className="text-lg font-bold tracking-tight uppercase">
							{tv("page_title")}
						</h1>

						<p className="w-full text-sm text-muted-foreground text-pretty md:w-10/12 xl:w-8/12">
							{tv("page_description")}
						</p>
					</div>
				</div>

				<Separator className="bg-border" />

				<Deferred
					data={["vouchers"]}
					fallback={<Skeleton className="w-2/12 h-4 rounded-sm" />}
				>
					<FiltersPanel
						pageURL={globalProps.adminPortal.router.adminPortal.vouchers.index}
						queryParams={queryParams}
						newVoucherHref={
							globalProps.adminPortal.router.adminPortal.vouchers.index
						}
						onBulkUploadOpen={handleBulkUploadOpen}
						packages={packagesData}
						voucherOwners={voucherOwners ?? []}
					/>
				</Deferred>

				<Deferred
					data={["vouchers"]}
					fallback={<Skeleton className="relative w-full h-32 rounded-xl" />}
				>
					<DataTable
						columns={columns}
						data={vouchersData}
						customPagination={() =>
							vouchersMeta ? (
								<ApptPagination
									metadata={vouchersMeta}
									label="vouchers"
									actions={{
										goToPrevpage: () => changePage("prev"),
										goToNextPage: () => changePage("next"),
										onChangeLimit: (value) => changePage("limit", value),
										goToPage: (value) => changePage("page", value),
										goToFirstPage: () => changePage("first"),
										goToLastPage: () => changePage("last"),
									}}
								/>
							) : null
						}
						subComponent={(row) => (
							<VoucherDetails
								row={row}
								onClose={() => handleRowClose(row)}
								onEdit={() => handleEditVoucher(row.original.id)}
								packagesById={packagesById}
								selectedVoucher={
									selectedVoucher && selectedVoucher.id === row.original.id
										? selectedVoucher
										: undefined
								}
							/>
						)}
						currentExpanded={currentExpanded}
					/>
				</Deferred>

				{createVoucherDialog.isOpen && (
					<ResponsiveDialog {...createVoucherDialog}>
						<NewVoucherFormFields
							packages={packagesData}
							forceMode={createVoucherDialog.forceMode}
							handleOpenChange={createVoucherDialog.onOpenChange}
							bonusRoEditable={bonusRoEditable}
						/>
					</ResponsiveDialog>
				)}

				{editVoucherDialog.isOpen && (
					<ResponsiveDialog {...editVoucherDialog}>
						<NewVoucherFormFields
							packages={packagesData}
							forceMode={editVoucherDialog.forceMode}
							handleOpenChange={editVoucherDialog.onOpenChange}
							voucher={voucherForEdit}
							bonusRoEditable={bonusRoEditable}
						/>
					</ResponsiveDialog>
				)}

				<AlertDialog
					open={isDeleteDialogOpen}
					onOpenChange={handleDeleteDialogOpenChange}
				>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>Delete voucher?</AlertDialogTitle>
							<AlertDialogDescription>
								{voucherPendingDelete ? (
									<>
										This action will permanently delete{" "}
										<span className="font-semibold">
											{voucherPendingDelete.name ?? voucherPendingDelete.code}
										</span>{" "}
										and all of its associated data.
									</>
								) : (
									"This action cannot be undone."
								)}
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel disabled={isDeleteLoading}>
								Cancel
							</AlertDialogCancel>
							<Button
								variant="destructive"
								disabled={isDeleteLoading}
								onClick={(event) => {
									event.preventDefault();
									handleConfirmDelete();
								}}
							>
								{isDeleteLoading ? (
									<>
										<Loader2 className="mr-2 size-4 animate-spin" />
										Deleting...
									</>
								) : (
									"Delete"
								)}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>

				{isBulkUploadOpen && (
					<BulkUploadDialog
						isOpen={isBulkUploadOpen}
						onOpenChange={(open) => {
							if (!open) {
								handleBulkUploadClose();
							}
						}}
						uploadUrl={`${vouchersIndexRoute}/bulk-upload`}
						templateUrl={`${vouchersIndexRoute}/download-template`}
					/>
				)}
			</PageContainer>
		</>
	);
}

function BonusRoBadge({ className }: { className?: string }) {
	return (
		<Badge
			variant="outline"
			className={cn(
				"border rounded-full text-xs font-semibold uppercase bg-violet-50 text-violet-700 border-violet-200",
				className,
			)}
		>
			Bonus RO
		</Badge>
	);
}

type VoucherColumnContext = {
	locale: Locale;
	tzDate: ContextFn<Date>;
	onEditVoucher: (voucherId: string | number) => void;
	onDeleteVoucher: (voucherId: string | number) => void;
};

function getColumns(
	handleRowExpandToggle: (row: Row<Voucher>) => void,
	{ locale, tzDate, onEditVoucher, onDeleteVoucher }: VoucherColumnContext,
): ColumnDef<Voucher>[] {
	return [
		{
			id: "expander",
			header: () => null,
			cell: ({ row }) => (
				<Button
					variant="ghost"
					size="icon"
					className="border size-8 shadow-none bg-card border-none"
					onClick={() => handleRowExpandToggle(row)}
					aria-label={row.getIsExpanded() ? "Collapse row" : "Expand row"}
				>
					{row.getIsExpanded() ? (
						<ChevronUp className="size-4" />
					) : (
						<ChevronDown className="size-4" />
					)}
				</Button>
			),
			enableSorting: false,
			enableHiding: false,
		},
		{
			accessorKey: "code",
			header: ({ column }) => (
				<DataTableColumnHeader column={column} title="Code" />
			),
			cell: ({ row }) => (
				<span className="font-semibold uppercase tracking-tight">
					{row.original.code}
				</span>
			),
		},
		{
			accessorKey: "isActive",
			header: ({ column }) => (
				<DataTableColumnHeader column={column} title="Status" />
			),
			cell: ({ row }) => (
				<div className="flex flex-wrap items-center gap-1.5">
					<Badge
						variant="outline"
						className={cn(
							"border rounded-full text-xs font-semibold uppercase",
							row.original.isActive
								? "bg-emerald-50 text-emerald-700 border-emerald-200"
								: "bg-rose-50 text-rose-700 border-rose-200",
						)}
					>
						{row.original.isActive ? "Active" : "Inactive"}
					</Badge>
					{row.original.adminOnly && (
						<Badge
							variant="outline"
							className="border rounded-full text-xs font-semibold uppercase bg-amber-50 text-amber-700 border-amber-200"
						>
							Staff only
						</Badge>
					)}
					{row.original.program === "BONUS_RO" && <BonusRoBadge />}
					{row.original.therapist && (
						<p className="w-full text-xs text-muted-foreground">
							Owner:{" "}
							<span className="font-semibold text-foreground">
								{row.original.therapist.name}
							</span>
						</p>
					)}
				</div>
			),
		},
		{
			accessorKey: "name",
			header: ({ column }) => (
				<DataTableColumnHeader column={column} title="Name" />
			),
			cell: ({ row }) => (
				<span className="font-medium line-clamp-2">{row.original.name}</span>
			),
		},
		{
			accessorKey: "description",
			header: ({ column }) => (
				<DataTableColumnHeader column={column} title="Description" />
			),
			meta: {
				headerClassName: "hidden md:table-cell",
				cellClassName: "hidden md:table-cell",
			},
			cell: ({ row }) => (
				<p className="max-w-xs text-sm text-muted-foreground line-clamp-2">
					{row.original.description || "N/A"}
				</p>
			),
		},
		{
			accessorKey: "validity",
			header: ({ column }) => (
				<DataTableColumnHeader column={column} title="Validity Period" />
			),
			meta: {
				headerClassName: "hidden lg:table-cell",
				cellClassName: "hidden lg:table-cell text-nowrap",
			},
			cell: ({ row }) => {
				const { validFrom, validUntil } = row.original;
				const formatDate = (date: string) =>
					format(new Date(date), "PPp", {
						locale,
						in: tzDate,
					});

				if (!validFrom && !validUntil) {
					return "N/A";
				}

				const from = validFrom ? formatDate(validFrom) : "No start";
				const until = validUntil ? formatDate(validUntil) : "No end";

				return (
					<span className="text-sm">
						{from} {validFrom && validUntil && "—"} {until}
					</span>
				);
			},
		},
		{
			id: "actions",
			header: () => <span className="sr-only">Actions</span>,
			meta: {
				headerClassName: "hidden md:table-cell",
				cellClassName: "hidden md:table-cell text-nowrap",
			},
			cell: ({ row }) => (
				<div className="flex justify-end">
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								aria-label="Open voucher actions"
							>
								<MoreHorizontal className="size-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuLabel>Actions</DropdownMenuLabel>
							<DropdownMenuSeparator />
							<DropdownMenuItem
								onSelect={(event) => {
									event.preventDefault();
									onEditVoucher(row.original.id);
								}}
							>
								<PencilIcon className="size-4" />
								Edit
							</DropdownMenuItem>
							<DropdownMenuSeparator />
							<DropdownMenuItem
								className="text-destructive focus:text-destructive"
								onSelect={(event) => {
									event.preventDefault();
									onDeleteVoucher(row.original.id);
								}}
							>
								<Trash2Icon className="size-4" />
								Remove
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			),
			enableSorting: false,
		},
	];
}

type VoucherDetailsProps = {
	row: Row<Voucher>;
	selectedVoucher?: Voucher;
	onClose: () => void;
	onEdit: () => void;
	packagesById: Record<string, Package>;
};

function VoucherDetails({
	row,
	selectedVoucher,
	onClose,
	onEdit,
	packagesById,
}: VoucherDetailsProps) {
	const { locale, tzDate } = useDateContext();
	const [showAllPackages, setShowAllPackages] = useState(false);
	const [isOrdersDialogOpen, setIsOrdersDialogOpen] = useState(false);

	const hasSelectedVoucher =
		selectedVoucher && selectedVoucher.id === row.original.id;
	const voucher = hasSelectedVoucher ? selectedVoucher : null;
	const voucherPackages = useMemo(() => {
		if (!voucher) return [];

		return (voucher.packages ?? []).map((pkg) => {
			const packageDetails = packagesById?.[String(pkg.id)];
			return {
				...pkg,
				details: packageDetails,
			};
		});
	}, [voucher, packagesById]);
	const packagesGroupedByService = useMemo(() => {
		if (!voucherPackages.length) return [];

		const groups = voucherPackages.reduce<
			Record<string, { serviceName: string; packages: typeof voucherPackages }>
		>((acc, pkg) => {
			const serviceName =
				pkg.details?.service?.name?.replaceAll("_", " ") || "Other Services";
			if (!acc[serviceName]) {
				acc[serviceName] = { serviceName, packages: [] };
			}
			acc[serviceName].packages.push(pkg);
			return acc;
		}, {});

		return Object.values(groups).sort((a, b) =>
			a.serviceName.localeCompare(b.serviceName),
		);
	}, [voucherPackages]);

	useEffect(() => {
		if (!voucher) return;
		setShowAllPackages(false);
	}, [voucher]);

	if (!voucher) {
		return (
			<div className="p-4 space-y-3 rounded-xl border bg-muted/30 text-sm md:p-6">
				<div className="space-y-1">
					<p className="text-xs font-semibold uppercase text-muted-foreground">
						Voucher Details
					</p>
					<h4 className="text-base font-bold tracking-tight">
						{row.original.name ?? row.original.code}
					</h4>
					<Deferred
						data={["selectedVoucher", "packages"]}
						fallback={<Skeleton className="w-full h-4 rounded-sm" />}
					>
						<p className="text-muted-foreground">
							More details for this voucher couldn't be loaded yet.
						</p>
					</Deferred>
				</div>

				<div className="mt-4">
					<Button
						variant="outline"
						size="sm"
						className="w-full md:w-fit"
						onClick={onClose}
					>
						Close
					</Button>
				</div>
			</div>
		);
	}

	const infoBlocks = [
		{ label: "Code", value: voucher.code },
		{
			label: "Discount",
			value: (() => {
				// Its own type and value are only a label (FIXED 300000 before backend#207, PER_PACKAGE 0 after).
				if (voucher.program === "BONUS_RO") {
					return (
						<div className="font-normal">
							<p className="font-semibold">
								Potongan per paket
								{Number(voucher.discountValue) > 0 &&
									` (hingga ${formatCurrency(voucher.discountValue)})`}
							</p>
							<p className="text-xs text-muted-foreground">
								Nominalnya diatur di tab Rules halaman Bonus RO.
							</p>
						</div>
					);
				}
				const type =
					voucher.discountType?.replaceAll("_", " ")?.toUpperCase() || "N/A";
				const value =
					voucher.discountType?.toUpperCase() === "PERCENTAGE"
						? `${formatPercentage((voucher.discountValue ?? 0) / 100)}`
						: formatCurrency(voucher.discountValue, { emptyValue: "N/A" });
				const maxDiscount = voucher.maxDiscountAmount
					? formatCurrency(voucher.maxDiscountAmount, { emptyValue: "" })
					: null;

				return (
					<div className="font-normal">
						<p className="font-semibold">
							{value} &mdash; ({type})
						</p>
						{maxDiscount && (
							<p className="text-xs text-muted-foreground">
								Max: <span className="font-semibold">{maxDiscount}</span>
							</p>
						)}
					</div>
				);
			})(),
		},
		{
			label: "Min Order Amount",
			value: formatCurrency(voucher.minOrderAmount, { emptyValue: "N/A" }),
		},
		{
			label: "Validity Period",
			value: (() => {
				const { validFrom, validUntil } = voucher;
				const formatDate = (date: string) =>
					format(new Date(date), "PPPp", { locale, in: tzDate });

				if (!validFrom && !validUntil) {
					return "N/A";
				}

				const from = validFrom ? formatDate(validFrom) : "No start";
				const until = validUntil ? formatDate(validUntil) : "No end";

				return `${from} ${validFrom && validUntil ? "—" : ""} ${until}`;
			})(),
		},
		{
			label: "Summary",
			value: (
				<div className="font-normal">
					<p className="text-muted-foreground">
						<span className="font-semibold">{voucher?.usedCount ?? 0}</span> of{" "}
						<span className="font-semibold">{voucher?.quota ?? 0}</span>{" "}
						booking(s) used
					</p>
					<p className="text-muted-foreground">
						Remaining quota:{" "}
						<span className="font-semibold">
							{voucher.quota != null && voucher.usedCount != null
								? Math.max(voucher.quota - voucher.usedCount, 0)
								: "N/A"}
						</span>{" "}
						booking(s)
					</p>
					{(voucher.usedCount ?? 0) > 0 ? (
						<Button
							variant="link"
							size="sm"
							className="self-start px-0 mt-1 h-auto text-xs font-semibold uppercase"
							onClick={() => setIsOrdersDialogOpen(true)}
						>
							<ReceiptTextIcon className="size-3.5" />
							View orders
						</Button>
					) : null}
				</div>
			),
		},
		// Last, so the Code / Discount / Validity rows keep their layout.
		...(voucher.therapist
			? [{ label: "Owner Therapist", value: voucher.therapist.name }]
			: []),
	];
	const VISIBLE_GROUP_LIMIT = 1;
	const visiblePackagesGroups = showAllPackages
		? packagesGroupedByService
		: packagesGroupedByService.slice(0, VISIBLE_GROUP_LIMIT);
	const remainingPackagesCount = packagesGroupedByService
		.slice(VISIBLE_GROUP_LIMIT)
		.reduce((total, group) => total + group.packages.length, 0);
	const hasMorePackages = remainingPackagesCount > 0;
	const showToggle = hasMorePackages || showAllPackages;

	return (
		<div className="p-4 space-y-4 rounded-xl border bg-card text-card-foreground md:p-6 text-sm">
			<div className="flex gap-3 justify-between">
				<div className="space-y-1">
					<p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
						Voucher Details
					</p>
					<h4 className="text-base font-bold tracking-tight">
						{voucher.name ?? voucher.code}
					</h4>
					<p className="text-muted-foreground">
						{voucher.description || "No additional description provided."}
					</p>
				</div>

				<div>
					<Badge
						variant="outline"
						className={cn(
							"inline-flex border rounded-full text-xs font-semibold uppercase mt-1.5",
							row.original.isActive
								? "bg-emerald-50 text-emerald-700 border-emerald-200"
								: "bg-rose-50 text-rose-700 border-rose-200",
						)}
					>
						{voucher.isActive ? "Active" : "Inactive"}
					</Badge>
					{voucher.adminOnly && (
						<Badge
							variant="outline"
							className="inline-flex border rounded-full text-xs font-semibold uppercase mt-1.5 ml-1.5 bg-amber-50 text-amber-700 border-amber-200"
						>
							Staff only
						</Badge>
					)}
					{voucher.program === "BONUS_RO" && (
						<BonusRoBadge className="inline-flex mt-1.5 ml-1.5" />
					)}
				</div>
			</div>

			<div className="grid gap-4 md:grid-cols-4">
				{infoBlocks.map((item) => (
					<div
						key={item.label}
						className={cn(
							"space-y-1 rounded-lg bg-background p-3",
							item.label === "Summary" || item.label === "Validity Period"
								? "col-span-full lg:col-span-2"
								: "",
							item.label === "Discount" ? "col-span-2" : "",
						)}
					>
						<p className="text-xs uppercase text-muted-foreground">
							{item.label}
						</p>

						{typeof item.value === "string" ? (
							<p className="font-semibold text-pretty">{item.value ?? "N/A"}</p>
						) : (
							item.value
						)}
					</div>
				))}
			</div>

			<div className="grid gap-4 md:grid-cols-12">
				<div className="space-y-2 col-span-full">
					<p className="text-xs font-semibold uppercase text-muted-foreground">
						Packages
					</p>
					{packagesGroupedByService.length ? (
						<div className="flex flex-col gap-2">
							<div className="flex flex-col gap-3 mt-1">
								{visiblePackagesGroups.map((group) => (
									<div
										key={group.serviceName}
										className="space-y-1 rounded-lg border border-border/60 bg-background/60 p-3"
									>
										<p className="text-xs font-semibold uppercase text-muted-foreground">
											{group.serviceName}
										</p>
										<div className="flex flex-wrap gap-2">
											{group.packages.map((item) => (
												<Badge
													key={item.id}
													variant="accent"
													className="flex flex-wrap items-center gap-1 px-3 py-2 text-left leading-tight whitespace-normal break-all"
												>
													<span className="text-sm font-semibold">
														{item.details?.name ||
															item.name ||
															`Package #${item.id}`}
													</span>
													<span className="text-xs italic text-muted-foreground">
														(
														{(item.details?.numberOfVisit ??
														item.number_of_visit)
															? `${item.details?.numberOfVisit ?? item.number_of_visit} visit(s)`
															: "N/A visits"}
														)
													</span>
												</Badge>
											))}
										</div>
									</div>
								))}
							</div>

							{showToggle ? (
								<Button
									variant="link"
									size="sm"
									className="self-start px-0 text-xs font-semibold uppercase"
									onClick={() => setShowAllPackages((prev) => !prev)}
								>
									{showAllPackages
										? "Show fewer packages"
										: `Show ${remainingPackagesCount} more package(s)`}
								</Button>
							) : null}
						</div>
					) : (
						<p className="text-sm text-muted-foreground">
							No packages linked to this voucher.
						</p>
					)}
				</div>
			</div>

			<div className="mt-4 flex flex-col md:flex-row md:justify-end gap-4 md:gap-2">
				<Button
					variant="ghost-primary"
					size="sm"
					className="w-full md:w-fit"
					onClick={onEdit}
				>
					<PencilIcon />
					Edit
				</Button>
				<Button
					variant="ghost-destructive"
					size="sm"
					className="w-full md:w-fit"
					onClick={onClose}
				>
					<Trash2Icon />
					Remove
				</Button>
				<Button
					variant="ghost"
					size="sm"
					className="w-full md:w-fit"
					onClick={onClose}
				>
					Close
				</Button>
			</div>

			<VoucherOrdersDialog
				voucherId={voucher.id}
				voucherCode={voucher.code}
				isOpen={isOrdersDialogOpen}
				onOpenChange={setIsOrdersDialogOpen}
			/>
		</div>
	);
}

// * filters panel component
const SELECT_FILTERS = [
	{
		key: "is_active",
		label: "Status",
		options: [
			{ value: "true", label: "Active" },
			{ value: "false", label: "Inactive" },
		],
	},
	{
		key: "discount_type",
		label: "Discount",
		options: [
			{ value: "PERCENTAGE", label: "Percentage" },
			{ value: "FIXED", label: "Fixed Amount" },
			{ value: "PER_PACKAGE", label: "Per paket (lihat rule Bonus RO)" },
		],
	},
	{
		key: "program",
		label: "Program",
		options: [
			{ value: "BONUS_RO", label: "Bonus RO" },
			{ value: "REGULAR", label: "Regular" },
		],
	},
	{
		key: "admin_only",
		label: "Audience",
		options: [
			{ value: "true", label: "Staff only" },
			{ value: "false", label: "Everyone" },
		],
	},
	{
		key: "validity",
		label: "Validity",
		options: [
			{ value: "RUNNING", label: "Running" },
			{ value: "UPCOMING", label: "Upcoming" },
			{ value: "EXPIRED", label: "Expired" },
		],
	},
	{
		key: "quota",
		label: "Quota",
		options: [
			{ value: "AVAILABLE", label: "Available" },
			{ value: "EXHAUSTED", label: "Used up" },
		],
	},
] as const;
const DYNAMIC_FILTER_KEYS = [
	"therapist_id",
	"service_id",
	"package_id",
] as const;

type FilterKey =
	| "code"
	| (typeof SELECT_FILTERS)[number]["key"]
	| (typeof DYNAMIC_FILTER_KEYS)[number];
type Filters = Record<FilterKey, string>;
type FilterOption = { value: string; label: string; group?: string };
type FilterDef = {
	key: FilterKey;
	label: string;
	options: readonly FilterOption[];
	searchable?: boolean;
	multiple?: boolean;
};

const FILTER_KEYS: FilterKey[] = [
	"code",
	...SELECT_FILTERS.map((f) => f.key),
	...DYNAMIC_FILTER_KEYS,
];
const EMPTY_FILTERS = Object.fromEntries(
	FILTER_KEYS.map((key) => [key, ""]),
) as Filters;
const ALL_OPTION = "ALL";
const optionText = (option: FilterOption) =>
	option.group ? `${option.group} · ${option.label}` : option.label;
// Multi-select filters travel comma-joined, the shape the controller splits.
const splitValues = (value: string) => (value ? value.split(",") : []);
// "FISIOHOME · Paket A, Paket B; WICARAKU · Paket C": each group named once.
function selectionText(options: readonly FilterOption[], values: string[]) {
	const byGroup = new Map<string, string[]>();
	for (const value of values) {
		const option = options.find((o) => o.value === value);
		const group = option?.group ?? "";
		byGroup.set(group, [...(byGroup.get(group) ?? []), option?.label ?? value]);
	}
	return [...byGroup]
		.map(([group, labels]) =>
			group ? `${group} · ${labels.join(", ")}` : labels.join(", "),
		)
		.join("; ");
}

function filtersFromQuery(queryParams?: Record<string, unknown>): Filters {
	return Object.fromEntries(
		FILTER_KEYS.map((key) => [key, String(queryParams?.[key] ?? "")]),
	) as Filters;
}

type FiltersPanelProps = {
	pageURL: string;
	queryParams?: Record<string, unknown>;
	newVoucherHref: string;
	onBulkUploadOpen: () => void;
	packages: Package[];
	voucherOwners: { id: string; name: string }[];
};

const FiltersPanel = memo(function FiltersPanel({
	pageURL,
	queryParams,
	newVoucherHref,
	onBulkUploadOpen,
	packages,
	voucherOwners,
}: FiltersPanelProps) {
	const isMobile = useIsMobile();
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<Filters>(() =>
		filtersFromQuery(queryParams),
	);
	// A package picked under one service would hide vouchers once the service changes.
	const setFilter = (key: FilterKey, value: string) =>
		setFilters((prev) => ({
			...prev,
			[key]: value,
			...(key === "service_id" && { package_id: "" }),
		}));

	const queryKey = JSON.stringify(filtersFromQuery(queryParams));
	useEffect(() => {
		setFilters(JSON.parse(queryKey));
	}, [queryKey]);

	const filtersInitializedRef = useRef(false);
	const lastAppliedFiltersRef = useRef<string | null>(null);

	const sanitizedFilters = useMemo(
		() =>
			Object.fromEntries(
				FILTER_KEYS.map((key) => [key, filters[key].trim() || null]),
			),
		[filters],
	);

	useEffect(() => {
		const serializedFilters = JSON.stringify(sanitizedFilters);

		if (!filtersInitializedRef.current) {
			filtersInitializedRef.current = true;
			lastAppliedFiltersRef.current = serializedFilters;
			return;
		}

		if (lastAppliedFiltersRef.current === serializedFilters) {
			return;
		}

		lastAppliedFiltersRef.current = serializedFilters;

		const { fullUrl, queryParams: nextQueryParams } = populateQueryParams(
			pageURL,
			{
				...sanitizedFilters,
				page: 1,
			},
		);

		router.get(fullUrl, nextQueryParams, {
			only: ["flash", "adminPortal", "vouchers", "vouchersMeta"],
			preserveScroll: true,
			preserveState: true,
			replace: true,
		});
	}, [pageURL, sanitizedFilters]);

	const selectedServiceIds = useMemo(
		() => splitValues(filters.service_id),
		[filters.service_id],
	);
	const filterDefs = useMemo<FilterDef[]>(
		() => [
			...SELECT_FILTERS,
			{
				key: "therapist_id",
				label: "Owner",
				searchable: true,
				options: voucherOwners.map((owner) => ({
					value: owner.id,
					label: owner.name,
				})),
			},
			{
				key: "service_id",
				label: "Service",
				searchable: true,
				multiple: true,
				options: [
					...new Map(
						packages
							.filter((pkg) => pkg.service)
							.map((pkg) => [
								String(pkg.service?.id),
								pkg.service?.name.replaceAll("_", " ") ?? "",
							]),
					),
				].map(([value, label]) => ({ value, label })),
			},
			{
				key: "package_id",
				label: "Package",
				searchable: true,
				multiple: true,
				options: packages
					.filter(
						(pkg) =>
							!selectedServiceIds.length ||
							selectedServiceIds.includes(String(pkg.service?.id)),
					)
					.map((pkg) => ({
						value: String(pkg.id),
						label: `${pkg.name} (${pkg.numberOfVisit} visit)`,
						group:
							pkg.service?.name?.replaceAll("_", " ") ?? "Standalone Packages",
					})),
			},
		],
		[voucherOwners, packages, selectedServiceIds],
	);

	const activeFilters = [
		...(filters.code.trim()
			? [{ key: "code" as const, label: "Search", value: filters.code.trim() }]
			: []),
		...filterDefs.flatMap((filter) => {
			const values = splitValues(filters[filter.key]);
			if (!values.length) return [];
			return [
				{
					key: filter.key,
					label: filter.label,
					value: selectionText(filter.options, values),
				},
			];
		}),
	];

	return (
		<div className="grid gap-4">
			<div className="z-10 flex gap-2 justify-between items-center">
				<Button
					variant={showFilters ? "default" : "outline"}
					size={isMobile ? "icon" : "default"}
					onClick={() => setShowFilters((prev) => !prev)}
				>
					<ListFilterIcon />
					{isMobile ? null : showFilters ? "Close" : "Filters"}
					{!showFilters && activeFilters.length > 0 && (
						<Badge variant="accent" className="px-1 rounded-full">
							{activeFilters.length}
						</Badge>
					)}
				</Button>

				<div className="flex gap-2">
					<Button
						variant="primary-outline"
						size={isMobile ? "icon" : "default"}
						onClick={onBulkUploadOpen}
					>
						<Upload />
						{isMobile ? null : "Bulk Upload"}
					</Button>
					<Button asChild size={isMobile ? "icon" : "default"}>
						<Link
							href={
								populateQueryParams(newVoucherHref, { new: "voucher" }).fullUrl
							}
						>
							<PlusIcon />
							{isMobile ? null : "Create Voucher"}
						</Link>
					</Button>
				</div>
			</div>

			<AnimatePresence initial={false} mode="wait">
				{showFilters ? (
					<motion.section
						key="voucher-filters"
						initial={{ opacity: 0, y: -20, filter: "blur(8px)" }}
						animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
						exit={{ opacity: 0, y: -10, filter: "blur(8px)" }}
						transition={{ duration: 0.25, ease: "easeOut" }}
					>
						<div className="p-3 border shadow-inner rounded-xl bg-background">
							<div className="grid gap-4 md:grid-cols-12">
								<div className="md:col-span-12 xl:col-span-4">
									<Input
										value={filters.code}
										onChange={(e) => setFilter("code", e.target.value)}
										placeholder="Search by code or name"
										className="bg-input"
									/>
								</div>

								{filterDefs.map((filter) => (
									<div key={filter.key} className="md:col-span-3 xl:col-span-2">
										{filter.searchable ? (
											<SearchableFilter
												filter={filter}
												value={filters[filter.key]}
												onChange={(value) => setFilter(filter.key, value)}
											/>
										) : (
											<Select
												value={filters[filter.key] || ALL_OPTION}
												onValueChange={(value) =>
													setFilter(
														filter.key,
														value === ALL_OPTION ? "" : value,
													)
												}
											>
												<SelectTrigger
													aria-label={`${filter.label} filter`}
													className="bg-input"
												>
													<SelectValue placeholder={`All ${filter.label}`} />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value={ALL_OPTION}>
														All {filter.label}
													</SelectItem>
													{filter.options.map((option) => (
														<SelectItem key={option.value} value={option.value}>
															{option.label}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										)}
									</div>
								))}
							</div>

							{activeFilters.length > 0 && (
								<div className="flex flex-wrap items-center gap-2 mt-4">
									<span className="text-xs font-medium text-muted-foreground">
										Active filters:
									</span>

									{activeFilters.map((filter) => (
										<Badge
											key={filter.key}
											variant="secondary"
											className="flex items-center gap-1 pl-2 pr-1 py-1 max-w-full whitespace-normal rounded-full text-[10px] uppercase tracking-wide"
										>
											<span>{filter.label}:</span>
											<span className="font-bold">{filter.value}</span>
											<button
												type="button"
												aria-label={`Remove ${filter.label} filter`}
												onClick={() => setFilter(filter.key, "")}
												className="inline-flex items-center justify-center rounded-full p-0.5 hover:bg-muted-foreground/15"
											>
												<XIcon className="h-3 w-3" />
											</button>
										</Badge>
									))}

									<Button
										variant="destructive-outline"
										size="sm"
										className="rounded-full"
										onClick={() => setFilters(EMPTY_FILTERS)}
									>
										Clear all filters
									</Button>
								</div>
							)}

							<div className="mt-4 flex flex-col gap-4 md:gap-2 md:flex-row md:justify-end">
								<Button
									variant="ghost"
									size="sm"
									className="w-full md:w-fit"
									onClick={() => setShowFilters(false)}
								>
									Close
								</Button>
							</div>
						</div>
					</motion.section>
				) : null}
			</AnimatePresence>
		</div>
	);
});

function SearchableFilter({
	filter,
	value,
	onChange,
}: {
	filter: FilterDef;
	value: string;
	onChange: (value: string) => void;
}) {
	const [open, setOpen] = useState(false);
	const selectedValues = splitValues(value);
	const selected = filter.options.filter((option) =>
		selectedValues.includes(option.value),
	);
	const groups = [
		...filter.options
			.reduce((acc, option) => {
				const group = option.group ?? "";
				acc.set(group, [...(acc.get(group) ?? []), option]);
				return acc;
			}, new Map<string, FilterOption[]>())
			.entries(),
	];
	const pick = (next: string) => {
		if (filter.multiple && next) {
			onChange(
				(selectedValues.includes(next)
					? selectedValues.filter((v) => v !== next)
					: [...selectedValues, next]
				).join(","),
			);
			return;
		}
		onChange(next);
		setOpen(false);
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="outline"
					aria-label={`${filter.label} filter`}
					className="w-full justify-between px-3 font-normal bg-input"
				>
					<span className="truncate">
						{selected[0]?.label ?? `All ${filter.label}`}
					</span>
					<span className="ml-auto flex shrink-0 items-center gap-1">
						{selected.length > 1 && (
							<Badge variant="accent" className="px-1 rounded-full">
								+{selected.length - 1}
							</Badge>
						)}
						<ChevronsUpDown className="opacity-50" />
					</span>
				</Button>
			</PopoverTrigger>
			<PopoverContent align="start" className="p-0">
				<Command>
					<CommandInput
						placeholder={`Search ${filter.label.toLowerCase()}...`}
					/>
					<CommandList>
						<CommandEmpty>No {filter.label.toLowerCase()} found.</CommandEmpty>
						<CommandGroup>
							<CommandItem
								value={`All ${filter.label}`}
								onSelect={() => pick("")}
							>
								All {filter.label}
							</CommandItem>
						</CommandGroup>
						{groups.map(([group, options]) => (
							<CommandGroup key={group} heading={group || undefined}>
								{options.map((option) => (
									<CommandItem
										key={option.value}
										value={`${optionText(option)} ${option.value}`}
										onSelect={() => pick(option.value)}
									>
										{option.label}
										<Check
											className={cn(
												"ml-auto h-4 w-4",
												selectedValues.includes(option.value)
													? "opacity-100"
													: "opacity-0",
											)}
										/>
									</CommandItem>
								))}
							</CommandGroup>
						))}
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
